/* Call this factory with countryCode as param to retrieve a list of address configurations for country */

// Import state lists for countries with dropdown
import US_STATES from '../../data/us.states.js';
import CA_PROVINCES from '../../data/ca.provinces.js';
import AU_STATES from '../../data/au.states.js';
import GB_COUNTIES from '../../data/gb.counties.js';

export var AddressConfigsFactory = (function() {
    /* Public Functions */

    var getAddressConfigs = function(countryCode) {
        switch (countryCode) {
        case 'US': {
            return USAddressConfigs;
        }
        case 'CA': {
            return CAAddressConfigs;
        }
        case 'GB': {
            return GBAddressConfigs;
        }
        case 'AU': {
            return AUAddressConfigs;
        }
        case 'NZ': {
            return NZAddressConfigs;
        }
        case 'FR': {
            return FRAddressConfigs;
        }
        case 'ES': {
            return ESAddressConfigs;
        }
        case 'DE': {
            return DEAddressConfigs;
        }
        case 'HK': {
            return HKAddressConfigs;
        }
        case 'MY': {
            return MYAddressConfigs;
        }
        case 'SG': {
            return SGAddressConfigs;
        }
        case 'TH': {
            return THAddressConfigs;
        }
        case 'NO': {
            return NOAddressConfigs;
        }
        default: {
            return USAddressConfigs;
        }
        }
    };

    /* Private Configurations */
    // common regexes that are used
    var commonSpecialCharRegex = /[\\\"\'\;\:\>\|\[\]\-_=+~`!€£¥₹₹@#\$%^<.,?{}/&*\(\)]$/i;
    var commonDigitRegex = /[^0-9]/g;

    // Americas
    var USAddressConfigs = {
        addressRequirements: {
            streetNumRequired: true,
            state: {
                // REQUIRED
                show: true,
                // REQUIRED
                dropdown: true,
                // required if dropdown is true
                stateList: US_STATES
            },
            postalCode: {
                // REQUIRED
                showZip: true,
                // REQUIRED
                minLength: 5,
                // REQUIRED
                maxLength: 10,
                // REQUIRED: when entering characters, this will restrict special chars from being entered
                restrictedChars: commonSpecialCharRegex,
                // REQUIRED: when a value is populated or pasted, this will remove all chars except unrestricted
                unrestrictedChars: commonDigitRegex,
                // REQUIRED: if true, will restrict alphabetical characters
                restrictAlpha: true,
                // REQUIRED: if true, will not allow a space to be entered
                // if false, will allow one space or one dash and will replace the dash with a space
                restrictSpaces: true,
                // REQUIRED: if true, will truncate the zip at truncateLength with separationChar
                truncate: true,
                // required if truncate is true
                truncateLength: 5,
                // required if truncate is true
                separationChar: '-',
                // REQUIRED: if patternCheck is true, will check the zipcode against patterns
                patternCheck: false,
                // required if patternCheck is true
                // patterns should have an array of regexes to check for
                patterns: [],
                // REQUIRED: if true, look for blacklist from configs
                blacklist: false,
                // only one error block should be true, the other should be false
                // REQUIRED: shows error if zip is not exactly minLength or maxLength
                // use this if you are truncating the zip
                showErrorBlock1: true,
                // REQUIRED: shows error if zip is not between minLength or maxLength
                showErrorBlock2: false
            },
            // These are for the state and zip fields
            // True will display both full width on the form
            // REQUIRED
            fullWidthDesktop: false,
            fullWidthIpad: true
        },
        qasValidation: false
    };

    var CAAddressConfigs = {
        addressRequirements: {
            streetNumRequired: true,
            state: {
                show: true,
                dropdown: true,
                stateList: CA_PROVINCES
            },
            postalCode: {
                showZip: true,
                minLength: 7,
                maxLength: 7,
                restrictedChars: commonSpecialCharRegex,
                unrestrictedChars: /[^a-zA-Z0-9]/g,
                restrictAlpha: false,
                restrictSpaces: true,
                // if true, will truncate the zip at truncateLength with separationChar
                truncate: true,
                truncateLength: 3,
                separationChar: ' ',
                patternCheck: false,
                blacklist: false,
                // Shows error if zip is not exactly minLength or maxLength
                showErrorBlock1: true,
                // Shows error if zip is not between minLength or maxLength
                showErrorBlock2: false
            },
            fullWidthDesktop: false,
            fullWidthIpad: true
        },
        qasValidation: true
    };

    // EMEA

    var GBAddressConfigs = {
        addressRequirements: {
            streetNumRequired: false,
            state: {
                show: true,
                dropdown: true,
                stateList: GB_COUNTIES
            },
            postalCode: {
                showZip: true,
                minLength: 6,
                maxLength: 8,
                restrictedChars: /[\\\"\'\;\:\>\|\[\]\_=+~`!€£¥₹₹@#\$%^<.,?{}/&*\(\)]$/i,
                unrestrictedChars: /[^a-zA-Z0-9\s-]/g,
                restrictAlpha: false,
                restrictSpaces: false,
                truncate: false,
                // if patternCheck is true, will check the zipcode against patterns
                patternCheck: true,
                patterns: ['^[a-zA-Z][a-zA-Z]\\d\\s\\d[a-zA-Z][a-zA-Z]$', '^[a-zA-Z]\\d\\s\\d[a-zA-Z][a-zA-Z]$',
                    '^[a-zA-Z]\\d\\d\\s\\d[a-zA-Z][a-zA-Z]$', '^[a-zA-Z][a-zA-Z]\\d\\d\\s\\d[a-zA-Z][a-zA-Z]$',
                    '^[a-zA-Z]\\d[a-zA-Z]\\s\\d[a-zA-Z][a-zA-Z]$',
                    '^[a-zA-Z][a-zA-Z]\\d[a-zA-Z]\\s\\d[a-zA-Z][a-zA-Z]$'],
                blacklist: false,
                // Shows error if zip is not exactly minLength or maxLength
                showErrorBlock1: false,
                // Shows error if zip is not between minLength or maxLength
                showErrorBlock2: true
            },
            fullWidthDesktop: false,
            fullWidthIpad: true
        },
        qasValidation: false
    };

    var FRAddressConfigs = {
        addressRequirements: {
            streetNumRequired: false,
            state: {
                show: false,
                dropdown: false
            },
            postalCode: {
                showZip: true,
                minLength: 5,
                maxLength: 5,
                restrictedChars: commonSpecialCharRegex,
                unrestrictedChars: commonDigitRegex,
                restrictAlpha: true,
                restrictSpaces: true,
                truncate: false,
                patternCheck: false,
                blacklist: false,
                // Shows error if zip is not exactly minLength or maxLength
                showErrorBlock1: true,
                // Shows error if zip is not between minLength or maxLength
                showErrorBlock2: false
            },
            fullWidthDesktop: true,
            fullWidthIpad: true
        },
        qasValidation: false
    };

    var ESAddressConfigs = {
        addressRequirements: {
            streetNumRequired: false,
            state: {
                show: false,
                dropdown: false
            },
            postalCode: {
                showZip: true,
                minLength: 5,
                maxLength: 5,
                restrictedChars: commonSpecialCharRegex,
                unrestrictedChars: commonDigitRegex,
                restrictAlpha: true,
                restrictSpaces: true,
                truncate: false,
                patternCheck: false,
                blacklist: true,
                // Shows error if zip is not exactly minLength or maxLength
                showErrorBlock1: false,
                // Shows error if zip is not between minLength or maxLength
                showErrorBlock2: true
            },
            fullWidthDesktop: true,
            fullWidthIpad: true
        },
        qasValidation: false
    };

    var NOAddressConfigs = {
        addressRequirements: {
            streetNumRequired: false,
            state: {
                show: false,
                dropdown: false
            },
            postalCode: {
                showZip: true,
                minLength: 4,
                maxLength: 4,
                restrictedChars: commonSpecialCharRegex,
                unrestrictedChars: commonDigitRegex,
                restrictAlpha: true,
                restrictSpaces: true,
                truncate: false,
                patternCheck: false,
                blacklist: false,
                // Shows error if zip is not exactly minLength or maxLength
                showErrorBlock1: true,
                // Shows error if zip is not between minLength or maxLength
                showErrorBlock2: false
            },
            fullWidthDesktop: true,
            fullWidthIpad: true
        },
        qasValidation: false
    };

    var DEAddressConfigs = {
        addressRequirements: {
            streetNumRequired: false,
            state: {
                show: false,
                dropdown: false
            },
            postalCode: {
                showZip: true,
                minLength: 5,
                maxLength: 5,
                restrictedChars: commonSpecialCharRegex,
                unrestrictedChars: commonDigitRegex,
                restrictAlpha: true,
                restrictSpaces: true,
                truncate: false,
                patternCheck: false,
                blacklist: false,
                // Shows error if zip is not exactly minLength or maxLength
                showErrorBlock1: true,
                // Shows error if zip is not between minLength or maxLength
                showErrorBlock2: false
            },
            // These are for the state and zip fields
            // True will display both full width on the form
            fullWidthDesktop: true,
            fullWidthIpad: true
        },
        qasValidation: false
    };

    // Pacific

    var AUAddressConfigs = {
        addressRequirements: {
            streetNumRequired: true,
            state: {
                show: true,
                dropdown: true,
                stateList: AU_STATES
            },
            postalCode: {
                showZip: true,
                minLength: 4,
                maxLength: 4,
                restrictedChars: commonSpecialCharRegex,
                unrestrictedChars: commonDigitRegex,
                restrictAlpha: true,
                restrictSpaces: true,
                truncate: false,
                patternCheck: false,
                blacklist: false,
                // Shows error if zip is not exactly minLength or maxLength
                showErrorBlock1: true,
                // Shows error if zip is not between minLength or maxLength
                showErrorBlock2: false
            },
            fullWidthDesktop: false,
            fullWidthIpad: true
        },
        qasValidation: true
    };

    var NZAddressConfigs = {
        addressRequirements: {
            streetNumRequired: true,
            state: {
                show: true,
                dropdown: false
            },
            postalCode: {
                showZip: true,
                minLength: 4,
                maxLength: 4,
                restrictedChars: commonSpecialCharRegex,
                unrestrictedChars: commonDigitRegex,
                restrictAlpha: true,
                restrictSpaces: true,
                truncate: false,
                patternCheck: false,
                blacklist: false,
                // Shows error if zip is not exactly minLength or maxLength
                showErrorBlock1: true,
                // Shows error if zip is not between minLength or maxLength
                showErrorBlock2: false
            },
            fullWidthDesktop: false,
            fullWidthIpad: false
        },
        qasValidation: false
    };

    // Greater China

    var HKAddressConfigs = {
        addressRequirements: {
            streetNumRequired: true,
            state: {
                show: true,
                dropdown: true
            },
            postalCode: {
                showZip: false
            },
            fullWidthDesktop: true,
            fullWidthIpad: true
        },
        qasValidation: false
    };

    // South-East Asia

    var MYAddressConfigs = {
        addressRequirements: {
            streetNumRequired: true,
            state: {
                show: false,
                dropdown: false
            },
            postalCode: {
                showZip: true,
                minLength: 5,
                maxLength: 5,
                restrictedChars: commonSpecialCharRegex,
                unrestrictedChars: commonDigitRegex,
                restrictAlpha: true,
                restrictSpaces: true,
                truncate: false,
                patternCheck: false,
                blacklist: false,
                // Shows error if zip is not exactly minLength or maxLength
                showErrorBlock1: true,
                // Shows error if zip is not between minLength or maxLength
                showErrorBlock2: false
            },
            fullWidthDesktop: true,
            fullWidthIpad: true
        },
        qasValidation: false
    };

    var SGAddressConfigs = {
        addressRequirements: {
            streetNumRequired: true,
            state: {
                show: false,
                dropdown: false
            },
            postalCode: {
                showZip: true,
                minLength: 6,
                maxLength: 6,
                restrictedChars: commonSpecialCharRegex,
                unrestrictedChars: commonDigitRegex,
                restrictAlpha: true,
                restrictSpaces: true,
                truncate: false,
                patternCheck: false,
                blacklist: false,
                // Shows error if zip is not exactly minLength or maxLength
                showErrorBlock1: true,
                // Shows error if zip is not between minLength or maxLength
                showErrorBlock2: false
            },
            fullWidthDesktop: true,
            fullWidthIpad: true
        },
        qasValidation: false
    };

    var THAddressConfigs = {
        addressRequirements: {
            streetNumRequired: true,
            state: {
                show: false,
                dropdown: false
            },
            postalCode: {
                showZip: true,
                minLength: 5,
                maxLength: 5,
                restrictedChars: commonSpecialCharRegex,
                unrestrictedChars: commonDigitRegex,
                restrictAlpha: true,
                restrictSpaces: true,
                truncate: false,
                patternCheck: false,
                blacklist: false,
                // Shows error if zip is not exactly minLength or maxLength
                showErrorBlock1: true,
                // Shows error if zip is not between minLength or maxLength
                showErrorBlock2: false
            },
            // These are for the state and zip fields
            // True will display both full width on the form
            fullWidthDesktop: true,
            fullWidthIpad: true
        },
        qasValidation: false
    };

    return {
        getAddressConfigs: getAddressConfigs
    };
})();



//////////////////
// WEBPACK FOOTER
// /Users/geoffsagers/dev/webcomponents/js/packages/common-account/src/util/address/address-configs.js
// module id = 46
// module chunks = 0